/**
 * a2ui SESSION processor (DEC-3) — folds an `a2ui_surface` card's append-only
 * `envelopes[]` log into a live surface (component map + reactive data model),
 * then resolves the surface's `root` into a renderable tree.
 *
 * The card is OUR framing; the envelope stream is the STANDARD a2ui 1.0 wire
 * (`{version:"v1.0", createSurface|updateComponents|updateDataModel|deleteSurface}`).
 * Replay is idempotent from rev 0: the api-server reducer tail-replaces the last
 * `updateDataModel` for a path and re-emits fills, so applying the whole log
 * always converges to the same surface (mirrors the reducer's live==replay test).
 */

import { createDataModel, evalDynamic, evalFormatString, getPointer, CORE_FUNCTIONS } from './core.js';

const MESSAGE_KEYS = ['createSurface', 'updateComponents', 'updateDataModel', 'deleteSurface'];

function envelopeKind(env) {
  if (!env || typeof env !== 'object') return null;
  for (const k of MESSAGE_KEYS) if (env[k] && typeof env[k] === 'object') return k;
  return null;
}

/**
 * Fold an envelope list into `{ surfaces: Map<surfaceId, Surface> }`. A Surface
 * is `{ surfaceId, catalogId, components: {id→node}, dataModel (plain object) }`.
 * Applying the same log twice yields an equal result (idempotent).
 */
export function processEnvelopes(envelopes) {
  const surfaces = new Map();
  const ensure = (surfaceId, catalogId) => {
    if (!surfaces.has(surfaceId)) {
      surfaces.set(surfaceId, { surfaceId, catalogId: catalogId || null, components: {}, dataModel: {} });
    }
    return surfaces.get(surfaceId);
  };

  for (const env of Array.isArray(envelopes) ? envelopes : []) {
    const kind = envelopeKind(env);
    if (!kind) continue;
    const msg = env[kind];
    const sid = msg.surfaceId;
    if (!sid) continue;

    if (kind === 'createSurface') {
      const s = ensure(sid, msg.catalogId);
      s.catalogId = msg.catalogId || s.catalogId;
      if (msg.dataModel && typeof msg.dataModel === 'object') s.dataModel = structuredClone(msg.dataModel);
      for (const c of msg.components || []) if (c && c.id) s.components[c.id] = c;
    } else if (kind === 'updateComponents') {
      const s = ensure(sid, msg.catalogId);
      for (const c of msg.components || []) if (c && c.id) s.components[c.id] = c;
    } else if (kind === 'updateDataModel') {
      const s = ensure(sid);
      const path = msg.path == null ? '/' : msg.path;
      // whole-object replace at path; the reducer already coalesces per-path, so
      // a simple set is idempotent under re-emit. A malformed pointer (a2ui
      // DataError) is a no-op rather than a render crash.
      try {
        const model = createDataModel(s.dataModel);
        model.set(path, msg.value);
        s.dataModel = model.snapshot();
      } catch { /* rejected updateDataModel — leave the model unchanged */ }
    } else if (kind === 'deleteSurface') {
      surfaces.delete(sid);
    }
  }
  return { surfaces };
}

/** Read the single surface out of an `a2ui_surface` card. */
export function surfaceFromCard(card, functions = CORE_FUNCTIONS) {
  const data = card?.data || {};
  const { surfaces } = processEnvelopes(data.envelopes || []);
  const surfaceId = data.surfaceId;
  const surface = surfaces.get(surfaceId) || [...surfaces.values()][0] || null;
  if (!surface) return null;
  surface._functions = functions;
  return surface;
}

/**
 * Resolve one component id in a surface into a plain render node:
 *   { id, component, props (resolved literals + text), children:[resolvedNodes],
 *     visible:boolean, region }
 * ChildList templates (`{ componentId, path }`) expand per data-model row with
 * an `@index` scope; static string arrays resolve by id. Unknown ids are dropped
 * (renderer keeps siblings).
 */
export function resolveNode(surface, id, scope = {}, seen = new Set()) {
  const raw = surface.components[id === '__root__' ? 'root' : id];
  if (!raw || seen.has(id)) return null;
  const model = surface.dataModel;
  const functions = surface._functions || CORE_FUNCTIONS;
  const guard = new Set(seen).add(id);

  const visible = resolveVisible(raw.visible, model, scope, functions);
  const props = {};
  let children = null;

  for (const [k, v] of Object.entries(raw)) {
    if (k === 'id' || k === 'component' || k === 'visible') continue;
    if (k === 'children') { children = resolveChildren(surface, v, scope, functions, guard); continue; }
    props[k] = resolveProp(v, model, scope, functions);
  }

  // SINGLE-CHILD / child-ref props (a2ui core `ComponentId` refs, not a ChildList):
  // Card.child, Modal.trigger/content, Button.child, Tabs.tabs[].child. The wire
  // value is a component id string — resolve it into a child node so the renderer
  // can draw it (otherwise a `Card` with only a `child` renders empty).
  for (const key of CHILD_REF_PROPS) {
    if (typeof props[key] === 'string' && surface.components[props[key]]) {
      props[key] = resolveNode(surface, props[key], scope, guard);
    }
  }
  if (Array.isArray(props.tabs)) {
    props.tabs = props.tabs.map((t) => (
      t && typeof t === 'object' && typeof t.child === 'string' && surface.components[t.child]
        ? { ...t, child: resolveNode(surface, t.child, scope, guard) }
        : t
    ));
  }

  // interactive core components carry a two-way `value` binding; stash the raw
  // RFC-6901 path so the renderer can write the user's edit back into the data
  // model (DEC-8), alongside the already-resolved current value.
  if (INTERACTIVE.has(raw.component) && raw.value && typeof raw.value === 'object' && typeof raw.value.path === 'string') {
    props._valuePath = raw.value.path;
  }
  // an Image/Video whose src is BOUND (to a pending /media/<slot>/url) shows our
  // skeleton until the data model fills, then paints — so mark it as media-bound.
  if (raw.component === 'Image' || raw.component === 'Video') {
    const b = raw.url != null ? raw.url : raw.src;
    if (b && typeof b === 'object' && (typeof b.path === 'string' || typeof b.call === 'string')) props._mediaBound = true;
  }

  return {
    id: raw.id,
    component: raw.component,
    props,
    children,
    visible,
    region: raw.region,
  };
}

const CHILD_REF_PROPS = ['child', 'trigger', 'content'];
const INTERACTIVE = new Set(['CheckBox', 'Slider', 'TextField', 'ChoicePicker', 'DateTimeInput']);

function resolveVisible(v, model, scope, functions) {
  if (v == null) return true;
  if (typeof v === 'boolean') return v;
  const out = evalDynamic(v, wrap(model), scope, functions);
  return out == null ? true : Boolean(typeof out === 'object' ? out.valid ?? out : out);
}

function resolveProp(v, model, scope, functions) {
  if (v == null) return v;
  if (typeof v !== 'object') return v;
  if (Array.isArray(v)) return v.map((x) => resolveProp(x, model, scope, functions));
  if (typeof v.call === 'string') {
    if (v.call === 'formatString' && v.args && typeof v.args.value === 'string') {
      return evalFormatString(v.args.value, wrap(model), scope, functions);
    }
    return evalDynamic(v, wrap(model), scope, functions);
  }
  if (typeof v.path === 'string' && !('call' in v)) {
    return evalDynamic(v, wrap(model), scope, functions);
  }
  // plain object literal — recurse so nested binds resolve
  const out = {};
  for (const [k, val] of Object.entries(v)) out[k] = resolveProp(val, model, scope, functions);
  return out;
}

function resolveChildren(surface, spec, scope, functions, seen) {
  if (spec == null) return [];
  if (Array.isArray(spec)) {
    return spec.map((cid) => resolveNode(surface, cid, scope, seen)).filter(Boolean);
  }
  // template: { componentId, path }
  if (typeof spec === 'object' && spec.componentId) {
    const arr = spec.path ? getPointer(surface.dataModel, spec.path) : null;
    if (!Array.isArray(arr)) return [];
    return arr.map((_, idx) => {
      const childScope = { base: spec.path.replace(/\/$/, '') + '/' + idx, index: idx };
      const node = resolveNode(surface, spec.componentId, childScope, seen);
      if (node) node.key = `${spec.componentId}_${idx}`;
      return node;
    }).filter(Boolean);
  }
  return [];
}

/** Wrap a plain data-model object so evalDynamic's `.get` path works. */
function wrap(model) {
  if (model && typeof model.get === 'function') return model;
  return { get: (p) => getPointer(model, p) };
}
